'use client';

import { c as _c } from "react/compiler-runtime";
import { useAccess } from '@repo/auth';
import { permissions } from '@repo/permissions';
import type { SavedViewSummary } from '@repo/schema/table-configuration';
import { useTableConfiguration } from '@repo/table/table-configuration';
import { Button, toast } from '@repo/ui/components';
import { DateFormatter } from '@repo/ui/date-formatter';
import { Trash2 } from '@repo/ui/icons';
import { type ReactNode, useState } from 'react';
import { ConfirmWarning } from './confirm-warning.tsx';
import { productError } from './product-error.ts';
import { ViewDetailsForm } from './view-details-form.tsx';
import { ViewSharing } from './view-sharing.tsx';
import { ViewTransfer } from './view-transfer.tsx';
import { organisationLabel, ownerLabel, sharedViaLabel, sharingLabel } from './saved-view-format.ts';
const Section = t0 => {
  const $ = _c(6);
  const {
    children,
    title
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <h3 className="text-xs font-semibold text-muted-foreground">{title}</h3>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1 || $[4] !== title) {
    t2 = <section aria-label={title} className="flex flex-col gap-3 border-t p-3 first:border-t-0" data-sentry-component="Section" data-sentry-source-file="manage-view.tsx">{t1}{children}</section>;
    $[2] = children;
    $[3] = t1;
    $[4] = title;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
const About = t0 => {
  const $ = _c(39);
  const {
    view
  } = t0;
  let t1;
  if ($[0] !== view.description) {
    t1 = view.description ? <p className="text-sm text-muted-foreground">{view.description}</p> : null;
    $[0] = view.description;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <dt className="text-muted-foreground">Owner</dt>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== view) {
    t3 = ownerLabel(view);
    $[3] = view;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <dd>{t3}</dd>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <dt className="text-muted-foreground">Shared with</dt>;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== view) {
    t6 = sharingLabel(view);
    $[8] = view;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t6) {
    t7 = <dd>{t6}</dd>;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <dt className="text-muted-foreground">Your access</dt>;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== view) {
    t9 = sharedViaLabel(view);
    $[13] = view;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t9) {
    t10 = <dd>{t9}</dd>;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <dt className="text-muted-foreground">Organisations</dt>;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== view) {
    t12 = organisationLabel(view);
    $[18] = view;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== t12) {
    t13 = <dd>{t12}</dd>;
    $[20] = t12;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <dt className="text-muted-foreground">Updated</dt>;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== view.updated_at) {
    t15 = <dd><DateFormatter date={view.updated_at} kind="datetime" data-sentry-element="DateFormatter" data-sentry-source-file="manage-view.tsx" /></dd>;
    $[23] = view.updated_at;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t10 || $[26] !== t13 || $[27] !== t15 || $[28] !== t4 || $[29] !== t7) {
    t16 = <dl className="grid grid-cols-[6.5rem_1fr] gap-x-2 gap-y-0.5 text-xs">{t2}{t4}{t5}{t7}{t8}{t10}{t11}{t13}{t14}{t15}</dl>;
    $[25] = t10;
    $[26] = t13;
    $[27] = t15;
    $[28] = t4;
    $[29] = t7;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== view) {
    t17 = ownerLabel(view);
    $[31] = view;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== t17) {
    t18 = <p className="text-xs text-muted-foreground">Only {t17} can change it. Save as new to keep your own copy.</p>;
    $[33] = t17;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== t1 || $[36] !== t16 || $[37] !== t18) {
    t19 = <Section title="About" data-sentry-element="Section" data-sentry-component="About" data-sentry-source-file="manage-view.tsx">{t1}{t16}{t18}</Section>;
    $[35] = t1;
    $[36] = t16;
    $[37] = t18;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  return t19;
};

/** Names the view and what deleting it takes from people; counts are left out, as Roles hide who. */
const deleteWarning = ({
  audience_summary: audience,
  name
}: SavedViewSummary) => {
  if (audience.mode === 'owner') return `Delete ${name}? This can’t be undone.`;
  const loses = audience.mode === 'all' ? 'Everyone loses it' : 'The people it’s shared with lose it';
  return `Delete ${name}? ${loses}, pins to it are removed, and this can’t be undone.`;
};

/**
 * The view page's footer: Delete, behind an inline warning instead of a dialog. Focus moves to the
 * warning's Cancel when it opens, and back to Delete view… when it is cancelled.
 */
export const DeleteView = t0 => {
  const $ = _c(14);
  const {
    confirm: t1,
    onDeleted,
    view
  } = t0;
  const confirm = t1 === undefined ? false : t1;
  const controller = useTableConfiguration();
  const [step, setStep] = useState(confirm ? "confirming" : "idle");
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState(null);
  let t2;
  if ($[0] !== controller || $[1] !== onDeleted || $[2] !== view) {
    t2 = async () => {
      setIsPending(true);
      setError(null);
      ;
      try {
        await controller.deleteView(view.id);
        onDeleted();
      } catch (t3) {
        const failure = t3;
        setError(productError(failure instanceof Error ? failure : new Error(String(failure))));
        setIsPending(false);
      }
    };
    $[0] = controller;
    $[1] = onDeleted;
    $[2] = view;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const remove = t2;
  let t3;
  if ($[4] !== isPending || $[5] !== remove || $[6] !== step || $[7] !== view) {
    t3 = step === "confirming" ? <ConfirmWarning confirmLabel="Delete" confirmVariant="destructive" disabled={isPending} onCancel={() => setStep("cancelled")} onConfirm={() => void remove()}>{deleteWarning(view)}</ConfirmWarning> : <Button autoFocus={step === "cancelled"} onClick={() => setStep("confirming")} size="sm" variant="destructive"><Trash2 /> Delete view…</Button>;
    $[4] = isPending;
    $[5] = remove;
    $[6] = step;
    $[7] = view;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== error) {
    t4 = error ? <p className="text-xs text-destructive" role="alert">{error}</p> : null;
    $[9] = error;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t3 || $[12] !== t4) {
    t5 = <footer aria-label="Delete" className="flex flex-col gap-2 border-t p-3" data-sentry-component="DeleteView" data-sentry-source-file="manage-view.tsx">{t3}{t4}</footer>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
};

/**
 * Everything about one Saved View: details, sharing and, for people who can list Users, owner
 * for its owner, and a read-only
 * summary for everyone else. Delete sits in the page footer.
 */
export const ManageView = t0 => {
  const $ = _c(22);
  const {
    onChange,
    view
  } = t0;
  const controller = useTableConfiguration();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      permissions: [permissions.users.list]
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    allowed: canListUsers
  } = useAccess(t1);
  const [transfer, setTransfer] = useState("closed");
  if (!view.is_owner) {
    let t2;
    if ($[1] !== view) {
      t2 = <About view={view} />;
      $[1] = view;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  let t2;
  if ($[3] !== controller || $[4] !== onChange || $[5] !== view.id) {
    t2 = async details => {
      onChange(await controller.updateDetails(view.id, details));
      toast.success("Details saved");
    };
    $[3] = controller;
    $[4] = onChange;
    $[5] = view.id;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t2 || $[8] !== view) {
    t3 = <Section title="Details" data-sentry-element="Section" data-sentry-source-file="manage-view.tsx"><ViewDetailsForm onSubmit={t2} submitLabel="Save details" view={view} data-sentry-element="ViewDetailsForm" data-sentry-source-file="manage-view.tsx" /></Section>;
    $[7] = t2;
    $[8] = view;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== onChange || $[11] !== view) {
    t4 = <Section title="Sharing" data-sentry-element="Section" data-sentry-source-file="manage-view.tsx"><ViewSharing onChange={onChange} view={view} data-sentry-element="ViewSharing" data-sentry-source-file="manage-view.tsx" /></Section>;
    $[10] = onChange;
    $[11] = view;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== canListUsers || $[14] !== onChange || $[15] !== transfer || $[16] !== view) {
    t5 = canListUsers ? <Section title="Owner"><div className="flex items-center justify-between gap-2 text-sm"><span>You</span>{transfer === "open" ? null : <Button autoFocus={transfer === "cancelled"} onClick={() => setTransfer("open")} size="sm" variant="outline">Transfer…</Button>}</div>{transfer === "open" ? <ViewTransfer onCancel={() => setTransfer("cancelled")} onTransferred={onChange} view={view} /> : null}</Section> : null;
    $[13] = canListUsers;
    $[14] = onChange;
    $[15] = transfer;
    $[16] = view;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== t3 || $[19] !== t4 || $[20] !== t5) {
    t6 = <div className="flex flex-col" data-sentry-component="ManageView" data-sentry-source-file="manage-view.tsx">{t3}{t4}{t5}</div>;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  return t6;
};